// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import Hidden from "@insite/mobius/Hidden";
import { Typography } from "@insite/mobius/index";
import XngZone from "@rugby/App/Components/Common/XngZone";
import ProductDetailsCategoryPopup from "@rugby/App/Components/ProductDetailsCategoryPopup/ProductDetailsCategoryPopup";
import { xCustomProductDetailsState } from "@rugby/App/Store/Reducers";
import { getLineNotes, getProductAdditionalInfos, getProductToggleTableMode } from "@rugby/App/Store/Selectors/CustomProductDetails";
import { productDetailsStyles } from "@rugby/Overrides/Widgets/ProductDetails/productDetails.style";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

const styles = productDetailsStyles;

const mapStateToProps = (state: ApplicationState & xCustomProductDetailsState) => ({
    permissions: state.context.permissions,
    lineNotes: getLineNotes(state),
    productAdditionalInfos: getProductAdditionalInfos(state),
    tableMode: getProductToggleTableMode(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasShellContext & HasProductContext;

const ProductDetailsView: React.FC<Props> = props => {
    const [show, setShow] = useState(false);
    const [disableAddToCart, setDisableAddToCart] = useState(false);

    let descElement: any = null;
    const handleSticky = () => {
        if (props.permissions) {
            return;
        }

        if (!descElement) {
            descElement = document.querySelector('[data-test-selector="productDetails_bottomSection"]');
            return;
        }
        if (window.scrollY > descElement?.offsetTop - 100) {
            setShow(true);
        } else {
            setShow(false);
        }
    };

    const productAdditionalInfos = props?.productAdditionalInfos;
    useEffect(() => {
        document.addEventListener("scroll", handleSticky);
        if (productAdditionalInfos?.itemSpecIsRequired === "True" && !props?.lineNotes) {
            setDisableAddToCart(true);
        }
    });

    useEffect(() => {
        if (productAdditionalInfos?.itemSpecIsRequired === "True" && !props?.lineNotes) {
            setDisableAddToCart(true);
        } else {
            setDisableAddToCart(false);
        }
    }, [props?.lineNotes]);

    return (
        <StyledWrapper as="div">
            <StyledWrapper as="div" {...styles.topSection} data-test-selector="productDetails_topSection">
                <Hidden {...styles.productTableToggleWrapper}>
                    <XngZone contentId={props.id} zoneName="productTableToggle" hint="Product Table Toggle Zone:" />
                </Hidden>
                <Hidden>
                    <XngZone contentId={props.id} zoneName="Content0" hint="Share Button Zone:" />
                </Hidden>
            </StyledWrapper>

            <StyledWrapper as="div" {...styles.mainContentWrapper}>
                <StyledWrapper as="div" {...styles.leftSection} data-test-selector="productDetails_leftColumn">
                    <StyledWrapper as="div" {...styles.heroImage}>
                        <XngZone contentId={props.id} zoneName="HeroPicture" hint="Hero Picture Zone:" />
                    </StyledWrapper>
                    <StyledWrapper as="div">
                        <XngZone contentId={props.id} zoneName="ProductPictures" hint="Product Pictures Zone:" />
                    </StyledWrapper>
                    <ProductDetailsCategoryPopup />
                </StyledWrapper>
                <StyledWrapper as="div" {...styles.rightSection} data-test-selector="productDetails_rightColumn">
                    <StyledWrapper as="div">
                        <XngZone contentId={props.id} zoneName="categoryName" hint="Category Name Zone:" />
                    </StyledWrapper>

                    <StyledWrapper as="div">
                        <XngZone contentId={props.id} zoneName="ProductDescription" hint="Product Description Zone:" />
                    </StyledWrapper>

                    <StyledWrapper as="div">
                        <XngZone contentId={props.id} zoneName="ProductDetails" hint="Product Details Zone:" />
                    </StyledWrapper>

                    {(props.shellContext.isEditing || props.tableMode) && (
                        <StyledWrapper as="div">
                            <XngZone contentId={props.id} zoneName="ProductTable" hint="Product Table Zone:" />
                        </StyledWrapper>
                    )}

                    {(props.shellContext.isEditing || !props.tableMode) && (
                        <>
                            <StyledWrapper as="div">
                                <XngZone contentId={props.id} zoneName="ProductPrice" hint="Product Price Zone:" />
                            </StyledWrapper>

                            <StyledWrapper as="div">
                                <XngZone contentId={props.id} zoneName="ProductVariant" hint="Product Variant Zone:" />
                            </StyledWrapper>

                            <StyledWrapper as="div">
                                <XngZone contentId={props.id} zoneName="ProductActions" hint="Product Actions Zone:" />
                            </StyledWrapper>
                        </>
                    )}

                    <StyledWrapper as="div">
                        <XngZone contentId={props.id} zoneName="ProductNoteLine" hint="Product NoteLine Zone:" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.bottomSection} as="div" data-test-selector="productDetails_bottomSection">
                <XngZone contentId={props.id} zoneName="Content3" hint="Product Details Zone:" />
            </StyledWrapper>

            <StyledWrapper as="div" className={show ? "show" : ""} {...styles.stickyWrapper}>
                <GridContainer {...styles.stickyContainer} gap={0}>
                    <StyledWrapper {...styles.stickyPictureDesc} data-test-selector="ProductDetailsSticky-Info">
                        <StyledWrapper as="div" {...styles.stickyHeroPicture}>
                            <XngZone contentId={props.id} zoneName="HeroPicture" hint="Hero Picture Zone:" />
                        </StyledWrapper>

                        <StyledWrapper as="div" {...styles.stickyProductDescription}>
                            <XngZone contentId={props.id} zoneName="ProductDescription" hint="Product Description Zone:" />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.stickyAction} data-test-selector="ProductDetailsSticky-action">
                        <StyledWrapper as="div" {...styles.stickyProductPrice}>
                            <XngZone contentId={props.id} zoneName="ProductPrice" hint="Product Price Zone:" />
                        </StyledWrapper>

                        <StyledWrapper
                            as="div"
                            {...styles.stickyProductActions}
                            className={disableAddToCart ? "disable-ATC" : ""}
                        >
                            <XngZone contentId={props.id} zoneName="ProductActions" hint="Product Actions Zone:" />
                        </StyledWrapper>
                    </StyledWrapper>
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withIsInShell(withProductContext(connect(mapStateToProps)(ProductDetailsView))),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
